import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import XPost, { XLogo } from './XPost';

// Post inteiro do building in public, aberto a partir do carrossel.
// Mesmo padrão do ProjectPeekModal: portal, Framer Motion, scroll lock,
// fecha com ESC ou clique fora.

const notionEasing = [0.16, 1, 0.3, 1];

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const panelVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.35, ease: notionEasing },
  },
  exit: {
    opacity: 0,
    scale: 0.97,
    y: 10,
    transition: { duration: 0.2, ease: 'easeIn' },
  },
};

const XPostModal = ({ item, onClose }) => {
  const isOpen = Boolean(item);

  // ESC to close + scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);

  const modal = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={{ duration: 0.25 }}
            onClick={onClose}
          />

          {/* Centering wrapper — flexbox avoids Framer Motion overriding CSS translate */}
          <div
            className="fixed inset-0 z-[61] flex items-center justify-center p-4 md:p-8 pointer-events-none"
            onClick={onClose}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-label="Post do building in public"
              className="w-full max-w-xl max-h-full flex flex-col rounded-2xl border border-white/10 shadow-2xl overflow-hidden pointer-events-auto"
              style={{
                background: 'rgba(24, 24, 27, 0.92)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
              }}
              variants={panelVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 sm:px-5">
                <div className="flex items-center gap-3">
                  <XLogo className="h-5 w-5 text-light-text" />
                  <span className="font-display font-bold">Post</span>
                </div>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
                  aria-label="Fechar"
                >
                  <XMarkIcon className="w-6 h-6 text-gray-400" />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="flex-1 overflow-y-auto modal-scrollbar">
                <XPost item={item} full />
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-5 border-t border-white/10 px-4 py-3 text-sm sm:px-5">
                <a
                  href={item.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-secondary-text transition-colors hover:text-light-text"
                >
                  adverse.com.br
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 font-semibold text-x-blue hover:underline"
                >
                  Abrir no X
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default XPostModal;
